import { SVButton, SVForm, SVPhoneInput, SVTextInput } from '@/components/core';
import { MESSAGES } from '@/constants/messages';
import { REGEX } from '@/constants/regex';
import { UpdatePhoneFields } from '@/types/common';
import showToast from '@/utils/toast';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

type ChangePhoneModalProps = {
  userType: 'Staff' | 'Customer' | 'Teammate';
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sendOtp: UseMutateAsyncFunction<unknown, Error, void, unknown>;
  changePhone: UseMutateAsyncFunction<
    unknown,
    Error,
    UpdatePhoneFields,
    unknown
  >;
  isChangePhonePending: boolean;
  scrollToTop: (behavior?: ScrollBehavior) => void;
};

export default function ChangePhoneModal({
  userType,
  open,
  setOpen,
  sendOtp,
  changePhone,
  isChangePhonePending,
  scrollToTop,
}: ChangePhoneModalProps) {
  const formMethods = useForm<UpdatePhoneFields>({
    defaultValues: {
      phone: '',
      confirmPhone: '',
      otp: '',
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open) {
      sendOtp();
      formMethods.reset();
    }
  }, [open]);

  async function manageChangeEmail(data: UpdatePhoneFields) {
    try {
      await changePhone(data);
      showToast({
        type: 'success',
        data: {
          content: `${userType} phone number has been successfully changed`,
        },
      });
      setOpen(false);
      scrollToTop();
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
      <SVForm<UpdatePhoneFields>
        methods={formMethods}
        onSubmit={manageChangeEmail}>
        <DialogContent>
          <Typography variant="h6" component="h2">
            Change Phone Number
          </Typography>
          <SVPhoneInput label="Phone" name="phone" />
          <SVPhoneInput
            label="Confirm Phone"
            name="confirmPhone"
            validateMatch="phone"
          />
          <SVTextInput
            label="One Time Password"
            name="otp"
            rules={{
              required: true,
              pattern: {
                regex: REGEX.CHECK_NUMBER,
                readablePatternName: 'code',
              },
            }}
          />
          <SVButton variant="outlined" onClick={() => sendOtp()} type="button">
            Resend Code
          </SVButton>
        </DialogContent>
        <DialogActions>
          <SVButton onClick={() => setOpen(false)} variant="tertiary">
            Back
          </SVButton>
          <SVButton
            type="submit"
            isLoading={isChangePhonePending}
            onClick={formMethods.handleSubmit(manageChangeEmail)}>
            Change
          </SVButton>
        </DialogActions>
      </SVForm>
    </Dialog>
  );
}
